import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Loader2, Send, Headphones, CreditCard } from 'lucide-react';
import { toast } from 'sonner';

/**
 * StudentChat — Instagram-style single thread between the student and their
 * teacher. Polls every 5s.
 *
 * `onRedeemCode(code)` is called when the student taps a recharge-card bubble, so
 * the host can switch to the wallet and auto-fill the charge-card field.
 */
const StudentChat = ({ apiUrl, token, compact = false, onRedeemCode }) => {
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  const authHeaders = useMemo(() => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  }), [token]);

  const fetchThread = useCallback(async () => {
    try {
      const r = await fetch(`${apiUrl}/api/student/messages`, { headers: authHeaders });
      if (r.ok) {
        const d = await r.json();
        setMessages(d.messages || []);
      }
    } catch { /* ignore */ }
    setLoading(false);
  }, [apiUrl, authHeaders]);

  useEffect(() => { fetchThread(); }, [fetchThread]);
  useEffect(() => {
    const t = setInterval(fetchThread, 5000);
    return () => clearInterval(t);
  }, [fetchThread]);
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const send = async () => {
    const text = draft.trim();
    if (!text) return;
    setSending(true);
    setDraft('');
    try {
      const r = await fetch(`${apiUrl}/api/student/messages`, {
        method: 'POST', headers: authHeaders, body: JSON.stringify({ text }),
      });
      if (r.ok) {
        const m = await r.json();
        setMessages((prev) => [...prev, m]);
      } else {
        const e = await r.json().catch(() => ({}));
        toast.error(e.detail || 'فشل إرسال الرسالة');
      }
    } catch { toast.error('حدث خطأ'); }
    setSending(false);
  };

  return (
    <div className={compact ? 'h-full flex flex-col' : 'space-y-4'}>
      {!compact && (
        <div>
          <h1 className="text-2xl font-bold text-[#412402]">الدعم الفني</h1>
          <p className="text-[#412402]/60 mt-1">تواصل مع الدعم الفني — نحن هنا لمساعدتك</p>
        </div>
      )}

      <div className={`flex flex-col border border-[#E8DFCF] overflow-hidden bg-white ${compact ? 'flex-1 rounded-none border-0' : 'h-[70vh] rounded-2xl'}`}>
        {/* Header (hidden in compact/floating — the launcher panel has its own) */}
        {!compact && (
          <div className="flex items-center gap-3 px-4 py-3 border-b border-[#E8DFCF] bg-[#FFF8E7]">
            <div className="w-10 h-10 rounded-full bg-[#EF9F27]/15 flex items-center justify-center">
              <Headphones className="w-5 h-5 text-[#EF9F27]" />
            </div>
            <div>
              <p className="font-semibold text-[#412402] text-sm">الدعم الفني</p>
              <p className="text-[11px] text-[#412402]/50">نحن هنا لمساعدتك</p>
            </div>
          </div>
        )}

        {/* Messages */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-2 bg-[#FCFAF5]">
          {loading ? (
            <div className="py-10 text-center"><Loader2 className="w-5 h-5 animate-spin text-amber-600 mx-auto" /></div>
          ) : messages.length === 0 ? (
            <p className="text-center text-xs text-[#412402]/40 py-10">لا توجد رسائل بعد</p>
          ) : messages.map((m) => (
            <div key={m.id} className={`flex ${m.sender_role === 'student' ? 'justify-start' : 'justify-end'}`}>
              <div className={`max-w-[75%] rounded-2xl px-3 py-2 text-sm ${
                m.sender_role === 'student'
                  ? 'bg-[#EF9F27] text-white rounded-br-sm'
                  : 'bg-white border border-[#E8DFCF] text-[#412402] rounded-bl-sm'
              }`}>
                <p className="whitespace-pre-wrap break-words">{m.text}</p>
                {/* Recharge-card bubble: tap to auto-fill the code into the wallet */}
                {m.kind === 'recharge_card' && m.card_code && (
                  <button
                    type="button"
                    onClick={() => {
                      try { navigator.clipboard?.writeText(m.card_code); } catch { /* ignore */ }
                      onRedeemCode?.(m.card_code);
                    }}
                    className="mt-2 w-full flex items-center justify-center gap-1.5 rounded-lg bg-green-700 hover:bg-green-800 text-white font-semibold px-3 py-1.5 text-xs"
                    data-testid="chat-redeem-code-btn"
                  >
                    <CreditCard className="w-3.5 h-3.5" />
                    اشحن رصيدي بهذا الكود ({m.card_code})
                  </button>
                )}
                <p className={`text-[10px] mt-0.5 ${m.sender_role === 'student' ? 'text-white/70' : 'text-[#412402]/40'}`}>
                  {new Date(m.created_at).toLocaleTimeString('ar-EG', { hour: '2-digit', minute: '2-digit' })}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Composer */}
        <div className="p-3 border-t border-[#E8DFCF] flex items-center gap-2">
          <Input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder=""
            data-testid="student-msg-input"
          />
          <Button onClick={send} disabled={sending || !draft.trim()} className="bg-[#EF9F27] hover:bg-[#D88E1F] text-white shrink-0">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default StudentChat;
